'use client';

import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'next/navigation';
import { Alert, Box, Typography } from '@mui/material';
import {
  EVENT_LEAD_FORM_GRID_SX,
  EventLeadFormSkeleton,
  LeadCaptureForm,
} from '@/components/EventLeadForm';
import MarketingInteriorLayout, {
  MARKETING_FORM_COLUMN_INNER_SX,
  MARKETING_FORM_COLUMN_SX,
  MARKETING_FORM_HERO_COLUMN_SX,
} from '@/components/home-landing/MarketingInteriorLayout';
import {
  FORM_PAGE_DESKTOP_BP,
  heroTextLegibility,
} from '@/components/design-studio/design-studio-glass';

const PAGE_TITLE = 'Contact Us';
const DEFAULT_SUBTITLE =
  'Share your details and our team will follow up about services, materials, or your project.';
const SUCCESS_MESSAGE =
  'Thank you! Your message has been submitted successfully. Our team will be in touch soon.';

const LOGO_SRC = '/imgs/NIICO-Logo.png';
const CONTACT_CONFIG_URL = '/t/campaigns/public/contact-form-config/';
const CONTACT_SUBMIT_URL = '/t/campaigns/public/contact-leads/';
const SUCCESS_REDIRECT_URL = '/contact?submitted=1';

function ContactFormShell({ children }) {
  return (
    <MarketingInteriorLayout heroBackground formGrid formGridSx={EVENT_LEAD_FORM_GRID_SX}>
      {children}
    </MarketingInteriorLayout>
  );
}

function ContactCopyColumn({ title, subtitle }) {
  return (
    <Box
      sx={{
        ...MARKETING_FORM_HERO_COLUMN_SX,
        textAlign: { xs: 'center', [FORM_PAGE_DESKTOP_BP]: 'left' },
      }}
    >
      <Typography
        component="h1"
        sx={{
          ...heroTextLegibility,
          fontWeight: 600,
          fontSize: {
            xs: 'clamp(1.75rem, 5vw, 2.5rem)',
            [FORM_PAGE_DESKTOP_BP]: '2.75rem',
            xl: '3.25rem',
          },
          lineHeight: 1.15,
          letterSpacing: '-0.02em',
          mb: { xs: 2, [FORM_PAGE_DESKTOP_BP]: 1.25 },
        }}
      >
        {title}
      </Typography>
      <Typography
        component="p"
        sx={{
          ...heroTextLegibility,
          fontWeight: 400,
          fontSize: { xs: '1rem', [FORM_PAGE_DESKTOP_BP]: '1.0625rem' },
          lineHeight: { xs: 1.65, [FORM_PAGE_DESKTOP_BP]: 1.55 },
          maxWidth: { [FORM_PAGE_DESKTOP_BP]: 420, xl: 480 },
          mx: { xs: 'auto', [FORM_PAGE_DESKTOP_BP]: 0 },
          opacity: 0.95,
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  );
}

function ContactFormMessageColumn({ severity, children }) {
  return (
    <>
      <ContactCopyColumn title={PAGE_TITLE} subtitle={DEFAULT_SUBTITLE} />
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          alignItems: { xs: 'stretch', [FORM_PAGE_DESKTOP_BP]: 'center' },
          justifyContent: { xs: 'stretch', [FORM_PAGE_DESKTOP_BP]: 'center' },
        }}
      >
        <Alert
          severity={severity}
          sx={{
            width: '100%',
            maxWidth: { [FORM_PAGE_DESKTOP_BP]: 520, xl: 560 },
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            color: 'common.white',
            border: '1px solid rgba(255, 255, 255, 0.35)',
            backdropFilter: 'blur(12px)',
            '& .MuiAlert-icon': { color: 'common.white' },
          }}
        >
          {children}
        </Alert>
      </Box>
    </>
  );
}

ContactFormShell.propTypes = {
  children: PropTypes.node.isRequired,
};

ContactCopyColumn.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
};

ContactFormMessageColumn.propTypes = {
  severity: PropTypes.oneOf(['error', 'warning', 'info', 'success']).isRequired,
  children: PropTypes.node.isRequired,
};

function ContactFormPageContent() {
  const searchParams = useSearchParams();
  const isSubmitted = searchParams.get('submitted') === '1';

  const [state, setState] = React.useState({ loading: true, error: null, data: null });

  React.useEffect(() => {
    if (isSubmitted) {
      setState({ loading: false, error: null, data: null });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(CONTACT_CONFIG_URL);
        if (cancelled) return;
        if (res.status === 404) {
          setState({
            loading: false,
            error: 'Contact form is not available.',
            data: null,
          });
          return;
        }
        if (!res.ok) {
          setState({
            loading: false,
            error: 'Could not load contact form.',
            data: null,
          });
          return;
        }
        const data = await res.json();
        setState({ loading: false, error: null, data });
      } catch {
        if (!cancelled) {
          setState({ loading: false, error: 'Network error.', data: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSubmitted]);

  if (isSubmitted) {
    return (
      <ContactFormShell>
        <ContactFormMessageColumn severity="success">{SUCCESS_MESSAGE}</ContactFormMessageColumn>
      </ContactFormShell>
    );
  }

  if (state.loading) {
    return <EventLeadFormSkeleton />;
  }

  if (state.error) {
    return (
      <ContactFormShell>
        <ContactFormMessageColumn severity="error">{state.error}</ContactFormMessageColumn>
      </ContactFormShell>
    );
  }

  const { event_code: eventCode, event_name: eventName, intents } = state.data;

  if (!intents?.length) {
    return (
      <ContactFormShell>
        <ContactFormMessageColumn severity="warning">
          No inquiry options are configured. Please try again later.
        </ContactFormMessageColumn>
      </ContactFormShell>
    );
  }

  return (
    <ContactFormShell>
      <ContactCopyColumn title={PAGE_TITLE} subtitle={DEFAULT_SUBTITLE} />
      <Box sx={MARKETING_FORM_COLUMN_SX}>
        <Box sx={MARKETING_FORM_COLUMN_INNER_SX}>
          <LeadCaptureForm
            variant="glass"
            eventCode={eventCode}
            eventName={eventName}
            logoSrc={LOGO_SRC}
            intentOptions={intents}
            submitUrl={CONTACT_SUBMIT_URL}
            formHeading="Contact Information"
            successRedirectUrl={SUCCESS_REDIRECT_URL}
          />
        </Box>
      </Box>
    </ContactFormShell>
  );
}

export default function ContactFormPage() {
  return (
    <Suspense fallback={<EventLeadFormSkeleton />}>
      <ContactFormPageContent />
    </Suspense>
  );
}
